import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Leaf, Sprout, Heart, Clock, Brain } from 'lucide-react';
import { motion } from 'framer-motion';
import { LegalFooter } from '@/components/LegalFooter';
import { SEO } from '@/components/SEO';

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About Plan My Garden — Why we built it"
        description="Plan My Garden is a personal gardening assistant that handles the planning so you can enjoy the growing. Learn our mission and values."
        path="/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Plan My Garden',
          url: 'https://planmygarden.app/about',
          description: 'Mission and values behind Plan My Garden — a personal gardening assistant that fits growing food into a busy life.',
        }}
      />

      {/* Header */}
      <header className="border-b border-border/40 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" aria-label="Go back" onClick={() => navigate(-1)} className="rounded-xl">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl garden-gradient shadow-soft">
              <Leaf className="h-4 w-4 text-primary-foreground" />
            </div>
            <h1 className="text-lg font-bold text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              About Plan My Garden
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 pb-24 space-y-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-4"
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl garden-gradient shadow-medium">
            <Sprout className="h-8 w-8 text-primary-foreground" />
          </div>
          <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Fit your garden into your life,<br />
            <span className="text-primary">not your life around your garden.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
            Plan My Garden was born from a simple frustration: growing your own food shouldn't take up all your mental space.
          </p>
        </motion.div>

        {/* Story */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="card-organic p-6 md:p-8 space-y-4"
        >
          <h3 className="text-xl font-semibold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why we built this
          </h3>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              We believe everyone should be able to grow the food they love eating fresh — whether that's juicy tomatoes, 
              crisp salad leaves, or your first ever courgette. But between seed-sowing windows, hardening-off schedules, 
              and knowing when to plant out, it can feel like you need a degree in horticulture just to get started.
            </p>
            <p>
              That's where <strong className="text-foreground">Plan My Garden</strong> comes in. Think of it as your virtual 
              gardening assistant — it does the heavy lifting of organising, scheduling, and remembering so you don't have to. 
              No more spreadsheets. No more missed sowing windows. No more guilt about forgetting to harden off your seedlings.
            </p>
            <p>
              You just show up and do what you love — <strong className="text-primary">garden</strong>.
            </p>
          </div>
        </motion.div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid gap-4 sm:grid-cols-3"
        >
          {[
            {
              icon: Clock,
              title: 'Save your time',
              desc: 'We generate a personalised schedule based on your setup, your region, and how much time you actually have.',
            },
            {
              icon: Brain,
              title: 'Free your mind',
              desc: "Stop trying to remember what needs doing and when. Plan My Garden keeps track so you don't have to.",
            },
            {
              icon: Heart,
              title: 'Enjoy gardening',
              desc: 'When the planning is handled, all that\'s left is the fun bit — getting your hands in the soil.',
            },
          ].map((item, i) => (
            <div key={i} className="card-organic p-5 text-center space-y-3">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <item.icon className="h-5 w-5 text-primary" />
              </div>
              <h4 className="font-semibold text-foreground text-sm">{item.title}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="text-center space-y-3"
        >
          <p className="text-muted-foreground">Ready to let Plan My Garden do the organising?</p>
          <Button onClick={() => navigate('/auth')} className="garden-gradient rounded-xl px-8">
            Get Started Free
          </Button>
        </motion.div>
      </main>

      <LegalFooter />
    </div>
  );
}
